import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Phone, Search, Loader2, MapPin, DollarSign } from 'lucide-react';
import { usePhoneNumbers } from '@/hooks/usePhoneNumbers';
import { 
  PhoneNumberProvider, 
  PhoneNumber, 
  AvailablePhoneNumber, 
  PhoneNumberSearchOptions 
} from '@/types/phoneNumber';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface PhoneNumberPurchaserProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchaseComplete: (phoneNumber: PhoneNumber) => void;
  provider: PhoneNumberProvider;
  onStateRefresh?: () => void;
  selectedVapiKey?: string;
  selectedVapiKeyName?: string;
}

export function PhoneNumberPurchaser({ 
  isOpen, 
  onClose, 
  onPurchaseComplete, 
  provider,
  onStateRefresh,
  selectedVapiKey,
  selectedVapiKeyName
}: PhoneNumberPurchaserProps) {
  const [searchOptions, setSearchOptions] = useState<PhoneNumberSearchOptions>({
    smsEnabled: true,
    voiceEnabled: true,
    limit: 20
  });
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [friendlyName, setFriendlyName] = useState('');
  const [numberToPurchase, setNumberToPurchase] = useState<AvailablePhoneNumber | null>(null);
  const [purchaseAlertOpen, setPurchseAlertOpen] = useState(false);

  const { 
    availableNumbers, 
    loading, 
    error, 
    searchAvailableNumbers, 
    purchasePhoneNumber,
    formatPhoneNumber 
  } = usePhoneNumbers();
  const { toast } = useToast();

  const handleSearch = () => {
    searchAvailableNumbers(searchOptions);
  };

  const handlePurchaseAlert = async(number: AvailablePhoneNumber) => {
    // Open the alert dialog to confirm purchase
    // If confirmed, call handlePurchase(number)
    setNumberToPurchase(number);
    setPurchseAlertOpen(true);
  }
  const handlePurchase = async (number: AvailablePhoneNumber) => {
    setPurchseAlertOpen(false);
    setNumberToPurchase(null);
    setPurchasing(number.phoneNumber);
    
    try {
      const purchasedNumber = await purchasePhoneNumber({
        phoneNumber: number.phoneNumber,
        provider,
        friendlyName: friendlyName || `SMS Number ${number.phoneNumber}`,
        vapiApiKey: selectedVapiKey,
        vapiApiKeyName: selectedVapiKeyName
      });
      
      if (purchasedNumber) {
        onPurchaseComplete(purchasedNumber);
        onStateRefresh?.();
        onClose();
      }
    } catch (error) {
      // Error is already handled in the hook
    } finally {
      setPurchasing(null);
    }
  };


  const updateSearchOptions = (key: keyof PhoneNumberSearchOptions, value: any) => {
    setSearchOptions(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Purchase Phone Number</DialogTitle>
          <DialogDescription>
            Search and purchase a new phone number from {provider}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="search" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="search">Search Numbers</TabsTrigger>
            <TabsTrigger value="settings">Search Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="search" className="space-y-4">
            <div className="flex gap-2">
              <Button 
                onClick={handleSearch} 
                disabled={loading}
                className="flex-shrink-0"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Search
              </Button>
              
              <Input
                placeholder="Friendly name (optional)"
                value={friendlyName}
                onChange={(e) => setFriendlyName(e.target.value)}
                className="max-w-xs"
              />
            </div>

            {error && (
              <Card className="border-destructive">
                <CardContent className="pt-6">
                  <p className="text-destructive text-sm">{error}</p>
                </CardContent>
              </Card>
            )}

            {availableNumbers.length > 0 && (
              <div className="grid gap-3 max-h-96 overflow-y-auto">
                {availableNumbers.map((number) => (
                  <Card key={number.phoneNumber} className="relative">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Phone className="w-4 h-4" />
                            <span className="font-medium text-lg">
                              {formatPhoneNumber(number.phoneNumber)}
                            </span>
                            {number.beta && (
                              <Badge variant="secondary" className="text-xs">
                                BETA
                              </Badge>
                            )}
                          </div>
                          
                          {(number.locality || number.region) && (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                              <MapPin className="w-3 h-3" />
                              <span>
                                {[number.locality, number.region].filter(Boolean).join(', ')}
                              </span>
                            </div>
                          )}

                          <div className="flex gap-1 mb-2">
                            {number.capabilities.map((capability) => (
                              <Badge key={capability} variant="outline" className="text-xs">
                                {capability.toUpperCase()}
                              </Badge>
                            ))}
                          </div>

                          {(number.monthlyPrice !== undefined || number.setupPrice !== undefined) && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <DollarSign className="w-3 h-3" />
                              {number.setupPrice !== undefined && (
                                <span>Purchase Price: ${number.setupPrice}</span>
                              )}
                              {number.monthlyPrice !== undefined && (
                                <span>Monthly: ${number.monthlyPrice}</span>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <Button
                            onClick={() => handlePurchaseAlert(number)}
                            disabled={purchasing === number.phoneNumber}
                            size="sm"
                            className="flex-1"
                          >
                            {purchasing === number.phoneNumber ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                Purchasing...
                              </>
                            ) : (
                              'Purchase'
                            )}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {!loading && availableNumbers.length === 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">No Numbers Found</CardTitle>
                  <CardDescription>
                    Try adjusting your search criteria or search in a different area.
                  </CardDescription>
                </CardHeader>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-4">
                <h3 className="font-medium">Search Criteria</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="areaCode">Area Code</Label>
                  <Input
                    id="areaCode"
                    placeholder="e.g., 415"
                    value={searchOptions.areaCode || ''}
                    onChange={(e) => updateSearchOptions('areaCode', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contains">Contains Digits</Label>
                  <Input
                    id="contains"
                    placeholder="e.g., 1234"
                    value={searchOptions.contains || ''}
                    onChange={(e) => updateSearchOptions('contains', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inLocality">City/Locality</Label>
                  <Input
                    id="inLocality"
                    placeholder="e.g., San Francisco"
                    value={searchOptions.inLocality || ''}
                    onChange={(e) => updateSearchOptions('inLocality', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inRegion">State/Region</Label>
                  <Input
                    id="inRegion"
                    placeholder="e.g., CA"
                    value={searchOptions.inRegion || ''}
                    onChange={(e) => updateSearchOptions('inRegion', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-medium">Capabilities</h3>
                
                <div className="flex items-center justify-between">
                  <Label htmlFor="smsEnabled">SMS Enabled</Label>
                  <Switch
                    id="smsEnabled"
                    checked={searchOptions.smsEnabled || false}
                    onCheckedChange={(checked) => updateSearchOptions('smsEnabled', checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="voiceEnabled">Voice Enabled</Label>
                  <Switch
                    id="voiceEnabled"
                    checked={searchOptions.voiceEnabled || false}
                    onCheckedChange={(checked) => updateSearchOptions('voiceEnabled', checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="mmsEnabled">MMS Enabled</Label>
                  <Switch
                    id="mmsEnabled"
                    checked={searchOptions.mmsEnabled || false}
                    onCheckedChange={(checked) => updateSearchOptions('mmsEnabled', checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="beta">Include Beta Numbers</Label>
                  <Switch
                    id="beta"
                    checked={searchOptions.beta || false}
                    onCheckedChange={(checked) => updateSearchOptions('beta', checked)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="limit">Results Limit</Label>
                  <Input
                    id="limit"
                    type="number"
                    min="1"
                    max="100"
                    value={searchOptions.limit || 20}
                    onChange={(e) => updateSearchOptions('limit', parseInt(e.target.value) || 20)}
                  />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
        <AlertDialog open={purchaseAlertOpen} onOpenChange={setPurchseAlertOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-blue-600">Purchase Phone Number?</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                  Are you sure you want to purchase the phone number {numberToPurchase?.phoneNumber}? Your Twillio account will be charged $1.15.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => {setPurchseAlertOpen(false); setNumberToPurchase(null);}}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => handlePurchase(numberToPurchase)}
              >
                Purchase
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
    
  );
}